// Shows a floating "Install App" button when Chrome/Edge fire the
// beforeinstallprompt event. On iOS Safari (which doesn't fire the event)
// we show a lightweight tip telling the user how to add-to-home-screen.
import { useEffect, useState } from "react";
import { Download, X, Share as ShareIcon } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "oht_install_dismissed_at";
const DISMISS_HOURS = 72;

function wasRecentlyDismissed() {
  const at = localStorage.getItem(DISMISS_KEY);
  if (!at) return false;
  const ageMs = Date.now() - Number(at);
  return ageMs < DISMISS_HOURS * 60 * 60 * 1000;
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: no prompt event — show tip after a small delay
    if (isIos()) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIos()) {
      setShowIosTip(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosTip(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-5 right-5 z-50 max-w-sm bg-white border border-slate-200 shadow-xl rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
        data-testid="install-app-banner"
      >
        <div className="w-10 h-10 rounded-lg gradient-blue-mild text-white flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 text-sm">
            Install Late Pass
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Add to your computer for a faster, app-like experience — opens in
            its own window with a desktop icon.
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              className="gradient-blue-mild text-white"
              onClick={install}
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              data-testid="install-dismiss-btn"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          onClick={dismiss}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIosTip && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <div className="font-semibold text-slate-800">
                Add to Home Screen
              </div>
              <button
                onClick={() => setShowIosTip(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
              <li>
                Tap the{" "}
                <span className="inline-flex items-center gap-1 font-medium">
                  Share <ShareIcon className="w-3.5 h-3.5" />
                </span>{" "}
                button in Safari
              </li>
              <li>
                Scroll down and choose{" "}
                <span className="font-medium">Add to Home Screen</span>
              </li>
              <li>Tap "Add" — the app icon will appear on your home screen</li>
            </ol>
            <Button
              className="w-full mt-4 gradient-blue-mild text-white"
              onClick={dismiss}
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
